/**
 * @author      Shivani Mehra
 * @date        Aug, 2024
 * @copyright   www.ibirdsservices.com
 */

import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  Container,
  Row,
  Table,
  Card,
  Form,
  Alert,
  Badge,
} from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify"; //npm i react-toastify --force
import "react-toastify/dist/ReactToastify.css";
import WhatsAppAPI from "../../api/WhatsAppAPI";
import {
  DatatableWrapper,
  Filter,
  Pagination,
  PaginationOptions,
  TableBody,
  TableHeader,
} from "react-bs-datatable";
import { useNavigate, Link } from "react-router-dom";
import Confirm from "../Confirm";
import jwt_decode from "jwt-decode";
import helper from "../common/helper";
import * as constants from "../../constants/CONSTANT";
import { FaTrash, FaEdit, FaPlus } from "react-icons/fa";
import { ShimmerTable } from "../common/ShimmerTable";
const Templates = ({ socket, selectedWhatsAppSetting }) => {
  const navigate = useNavigate();
  const [category, setCategory] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [body, setBody] = useState([]);
  // console.log("🚀 ~ Templates ~ body:", body);
  const [templateDelete, setTemplateDelete] = useState(false);
  const [rowData, setRowData] = useState();
  const [isSpinner, setIsSpinner] = useState(false);
  const [userInfo, setUserInfo] = useState({});
  const categories = ["AUTHENTICATION", "UTILITY", "MARKETING"];
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    if (!socket) return;

    const showAlert = (type, data) => {
      const newAlert = { id: Date.now(), type, data };
      setAlerts((prev) => [...prev, newAlert]);

      // Auto-remove after 5 minutes
      setTimeout(() => {
        setAlerts((prev) => prev.filter((a) => a.id !== newAlert.id));
      }, 300000);
    };

    const handleStatusUpdate = (data) => {
      fetchAllTemplates(selectedWhatsAppSetting);
      showAlert("status", data);
    };

    const handleCategoryUpdate = (data) => {
      showAlert("category", data);
    };

    socket.on("template_status_update", handleStatusUpdate);
    socket.on("template_category_update", handleCategoryUpdate);

    return () => {
      socket.off("template_status_update", handleStatusUpdate);
      socket.off("template_category_update", handleCategoryUpdate);
    };
  }, [socket, selectedWhatsAppSetting]);

  useEffect(() => {
    if (selectedWhatsAppSetting) {
      fetchAllTemplates(selectedWhatsAppSetting);
    } else {
      setIsSpinner(true);
    }
  }, [selectedWhatsAppSetting]);

  const onFilterType = (event) => {
    const selectedCategory = event.target.value;
    setCategory(selectedCategory);

    // Apply filter based on category
    if (selectedCategory === "") {
      setFilteredData(body); // If no category selected, show all data
    } else {
      setFilteredData(
        body.filter(
          (data) =>
            data.category.toLowerCase() === selectedCategory.toLowerCase(),
        ),
      );
    }
  };

  const fetchAllTemplates = async (selectedWhatsAppSetting) => {
    let user = jwt_decode(sessionStorage.getItem("token"));
    setUserInfo(user);

    const result = await WhatsAppAPI.getAllTemplates(selectedWhatsAppSetting);
    //console.log("🚀 ~ fetchAllTemplates ~ result:", result);
    if (result.error) {
      toast.error(result?.error?.message);
      setBody([]);
      setFilteredData([]);
      setIsSpinner(true);
    } else {
      if (result?.data && result) {
        //console.log("🚀 ~ fetchAllTemplates ~ result?.data:", result?.data);
        const transformedData = result?.data.map((row) => {
          // Check for CAROUSEL type
          const carouselComp = row.components.find(
            (component) =>
              component.type === "CAROUSEL" || component.type === "carousel",
          );
          const mainBody = row.components.find(
            (component) => component.type === "BODY",
          );

          if (carouselComp && Array.isArray(carouselComp.cards)) {
            // Map each card to a normalized structure
            const cards = carouselComp.cards.map((card) => {
              const header =
                card.components.find((c) => c.type === "HEADER") || {};
              const body = card.components.find((c) => c.type === "BODY") || {};
              const buttonsComp = card.components.find(
                (c) => c.type === "BUTTONS",
              );
              const buttons = buttonsComp?.buttons || [];

              const cardObj = {
                header_type: header.format || "",
                header_text: header.format === "TEXT" ? header.text : "",
                header_image_url:
                  header.format === "IMAGE"
                    ? header.example?.header_handle?.[0] || header.handle || ""
                    : "",
                header_document_url:
                  header.format === "DOCUMENT"
                    ? header.example?.header_handle?.[0] || ""
                    : "",
                header_video_url:
                  header.format === "VIDEO"
                    ? header.example?.header_handle?.[0] || ""
                    : "",
                body_text: body.text || "",
                body_text_example: body.example?.body_text?.[0] || [],
                buttons: buttons
                  .map((element) => ({
                    type: element.type,
                    text: element.text,
                    ...(element.type === "PHONE_NUMBER" && {
                      phone_number: element.phone_number,
                    }),
                    ...(element.type === "URL" && { url: element.url }),
                    example: element.example?.[0] || "",
                  }))
                  .filter((item) => item.text && item.type),
              };
              //console.log("[Templates.js] Carousel card mapped:", cardObj);
              return cardObj;
            });

            const transformed = {
              id: row.id,
              name: row.name,
              templatename: row.name.replace(/_/g, " "),
              language: row.language,
              status: row.status,
              category: row.category,
              template_type: "carousel",
              message_body: mainBody?.text || "",
              example_body_text: mainBody?.example?.body_text || [],
              cards, // <-- This is the important part!
            };
            //console.log(
            //   "[Templates.js] Carousel template mapped:",
            //   transformed
            // );
            return transformed;
          }

          const header =
            row.components.find((component) => component.type === "HEADER") ||
            {};
          const body =
            row.components.find((component) => component.type === "BODY") || {};
          const footer =
            row.components.find((component) => component.type === "FOOTER") ||
            {};
          const buttons =
            row.components.find((component) => component.type === "BUTTONS")
              ?.buttons || [];

          return {
            id: row.id,
            name: row.name,
            templatename: row.name.replace(/_/g, " "),
            language: row.language,
            status: row.status,
            category: row.category,
            header: header.format || "",

            header_text: header.format === "TEXT" ? header.text : "",
            header_image_url:
              header.format === "IMAGE"
                ? header.example?.header_handle?.[0] || header.handle || ""
                : "",
            header_document_url:
              header.format === "DOCUMENT"
                ? header.example?.header_handle?.[0] || header.handle || ""
                : "",
            header_video_url:
              header.format === "VIDEO"
                ? header.example?.header_handle?.[0] || header.handle || ""
                : "",

            message_body: body.text || "",
            example_body_text: body.example?.body_text || [],
            footer: footer.text || "",

            add_security_recommendation:
              body.add_security_recommendation || false,
            code_expiration_minutes: footer.code_expiration_minutes || null,

            // Button details extraction
            buttons: buttons
              .map((element) => {
                if (element.type === "OTP") {
                  return {
                    type: element.type,
                    otp_type: element.otp_type,
                    supported_apps:
                      element.supported_apps?.map((app) => ({
                        package_name: app.package_name,
                        signature_hash: app.signature_hash,
                      })) || [],
                  };
                } else {
                  return {
                    type: element.type,
                    text: element.text,
                    ...(element.type === "PHONE_NUMBER" && {
                      phone_number: element.phone_number,
                    }),
                    ...(element.type === "URL" && {
                      url: element.url,
                      ...(Array.isArray(element.example) &&
                        element.example.length > 0 && {
                          example: element.example,
                        }),
                    }),
                  };
                }
              })
              .filter((item) => item.text && item.type),
          };
        });
        //console.log(
        //   "🚀 ~ fetchAllTemplates ~ transformedData:",
        //   transformedData
        // );

        setBody(transformedData);
        setFilteredData(transformedData);
        setIsSpinner(true);
      } else {
        setBody([]);
        setFilteredData([]);
        setIsSpinner(true);
      }
    }
  };

  // Create table headers consisting of 4 columns.
  const labels = { beforeSelect: " " };
  const header = [
    {
      title: "Name",
      prop: "templatename",
      isFilterable: true,
      cell: (row) => {
        const capitalizedName = row.templatename
          ? row.templatename
              .toLowerCase()
              .replace(/\b\w/g, (char) => char.toUpperCase())
          : "";

        return (
          <Link to={"/whatsapp_template/view"} state={row}>
            {capitalizedName}
          </Link>
        );
      },
    },
    // { title: "name", prop: "templatename", isFilterable: true, },
    {
      title: "category",
      prop: "category",
      isFilterable: true,
      cell: (row) =>
        (() => {
          const cat = String(row.category || "—").toUpperCase();
          let tone = 1;
          if (cat.includes("HARD")) tone = 0;
          else if (cat.includes("SOFT")) tone = 5;
          else if (cat.includes("PROF")) tone = 2;
          else if (cat.includes("SERV")) tone = 3;
          else if (cat.includes("GRAIN") || cat.includes("SEED")) tone = 4;
          return (
            <Badge
              bg="light"
              className={`pill-soft pill-tone-${tone} text-uppercase`}
            >
              {row.category || "—"}
            </Badge>
          );
        })(),
    },
    { title: "language", prop: "language", isFilterable: true },
    {
      title: "Status",
      prop: "status",
      isFilterable: true,
      cell: (row) => {
        const s = row.status || "";
        const tone =
          s === "APPROVED" ? 0 : s === "PENDING" ? 4 : s === "REJECTED" ? 3 : 1;
        if (s === "APPROVED") {
          return (
            <Badge bg="light" className={`pill-soft pill-tone-${tone}`}>
              {s}
            </Badge>
          );
        }
        if (s === "PENDING") {
          return (
            <Badge bg="light" className={`pill-soft pill-tone-${tone}`}>
              {s}
            </Badge>
          );
        }
        if (s === "REJECTED") {
          return (
            <Badge bg="danger" className="px-2 py-1 fw-semibold">
              {s}
            </Badge>
          );
        }
        return (
          <Badge bg="light" className={`pill-soft pill-tone-${tone}`}>
            {s || "—"}
          </Badge>
        );
      },
    },
    {
      title: "Actions",
      prop: "id",
      cell: (row) => (
        <>
          {/* {row.status === "APPROVED" && (
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => editTemplate({ row })}
              title="Edit"
            >
              <FaEdit />
            </Button>
          )} */}
          <Button
            className="btn-action-icon btn-action-danger mx-2"
            variant="light"
            onClick={() => removeSelectedRow({ row })}
            title="Delete"
          >
            <i className="fa-regular fa-trash-can"></i>
          </Button>
          <Button
            className="btn-action-icon mx-2"
            variant="light"
            onClick={() => cloneRecord({ row })}
            // onClick={() => cloneSelectedRow(row)}
            title="Clone"
          >
            <i className="fa-solid fa-clone"></i>
          </Button>
        </>
      ),
    },
  ].filter(Boolean);

  const cloneRecord = (data) => {
    navigate(`/whatsapp_template/add`, {
      state: { ...data?.row, type: "clone" },
    });
  };

  // add new tempate
  const addNewTemplate = () => {
    navigate(`/whatsapp_template/add`);
  };

  const editTemplate = (data) => {
    navigate(`/whatsapp_template/add`, { state: data.row });
  };

  // delete template
  const removeSelectedRow = (data) => {
    setTemplateDelete(true);

    setRowData(data.row);
  };
  const deleteTemplate = async () => {
    if (rowData.id && rowData.name) {
      const result = await WhatsAppAPI.deleteTemplateRecord(
        rowData.id,
        rowData.name,
        selectedWhatsAppSetting,
      );
      if (result.success) {
        setTemplateDelete(false);
        toast.success("Template deleted successfully.");
        fetchAllTemplates(selectedWhatsAppSetting);
      } else {
        setTemplateDelete(false);
        toast.error(result.error.message);
      }
    }
  };

  return (
    <>
      <Container className="mt-5">
        <Row className="mx-5 mb-4">
          <Card className="p-4 header-bg shadow-sm border-0 rounded-3">
            <h5 className="mb-2 fw-bold">💡 Quick Guide</h5>
            <small>
              WhatsApp templates allow businesses to send structured,
              pre-approved messages such as alerts, updates, and reminders. To
              ensure successful delivery, templates must comply with Meta’s
              policies and follow the approved formatting rules.
              <br></br>
              👉 For detailed guidance, check the official{" "}
              <a
                target="_blank"
                href="https://developers.facebook.com/docs/whatsapp/updates-to-pricing/new-template-guidelines/"
                rel="noreferrer"
              >
                Meta Template Guidelines
              </a>
            </small>
          </Card>
        </Row>
        <Row className="mx-5 mb-4">
          {alerts?.map((alert) => (
            <Alert
              key={alert?.id}
              variant={
                alert?.type === "status"
                  ? alert?.data?.status === "APPROVED"
                    ? "success"
                    : "warning"
                  : "info"
              }
              dismissible
              onClose={() =>
                setAlerts((prev) => prev.filter((a) => a?.id !== alert?.id))
              }
            >
              {alert?.type === "status" ? (
                <>
                  <strong>Template {alert?.data?.status}!</strong> <br />
                  {alert?.data?.message_template_name} (
                  {alert?.data?.message_template_language})
                  {alert?.data?.reason && <> – Reason: {alert?.data?.reason}</>}
                </>
              ) : (
                <>
                  <strong>Category Updated!</strong> <br />
                  {alert?.data?.message_template_name} moved to{" "}
                  <b>{alert?.data?.new_category}</b>
                </>
              )}
            </Alert>
          ))}
        </Row>
        <Row className="mx-5 g-0">
          <Col lg={12} className="mb-3">
            <Row className="g-0">
              <Col lg={12}>
                <div className="bg-white shadow-sm border-0 rounded-3 p-4">
                  <h5 className="fw-semibold mb-1">📦 All Templates</h5>

                  {isSpinner ? (
                    <DatatableWrapper
                      body={body}
                      headers={header}
                      paginationOptionsProps={{
                        initialState: {
                          rowsPerPage: 10,
                          options: [5, 10, 15, 20],
                        },
                      }}
                    >
                      <Row className="mb-3">
                        <Col
                          lg={3}
                          sm={10}
                          xs={10}
                          className="d-flex flex-col justify-content-end align-items-end"
                        >
                          <Filter className="p-2" />
                        </Col>
                        <Col
                          lg={4}
                          sm={2}
                          xs={2}
                          className="d-flex flex-col justify-content-start align-items-start"
                        >
                          <PaginationOptions labels={labels} />
                        </Col>
                        <Col
                          lg={5}
                          sm={12}
                          xs={12}
                          className="mt-2 d-flex flex-col justify-content-end align-items-end"
                        >
                          <Button
                            className="btn btn-sm"
                            variant="outline-secondary"
                            onClick={addNewTemplate}
                          >
                            <FaPlus /> Add New Template
                          </Button>
                        </Col>
                      </Row>

                      <div className="table-responsive">
                        <Table
                          striped
                          bordered
                          className="related-list-table"
                          responsive="sm"
                        >
                          <TableHeader />
                          <TableBody />
                        </Table>
                      </div>
                      <Pagination />
                    </DatatableWrapper>
                  ) : (
                    <ShimmerTable row={10} col={4} />
                  )}
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
      {templateDelete && (
        <Confirm
          show={templateDelete}
          onHide={() => setTemplateDelete(false)}
          deleteTemplate={deleteTemplate}
          title="Confirm Delete"
          message="You are going to delete the record. Are you sure?"
          table="template"
        />
      )}

      <ToastContainer autoClose={30000} />
    </>
  );
};

export default Templates;
